import React from 'react';

import SequenceControl, {sequencePosition} from './SequenceControl.js';

// ============================================================================
// Page frame — PageHeader, RecordHeader, SequenceNav, PageFrame, usePageTitle
// (Design Constitution N07, N08, N09, R01, P01, Q16, Q41)
// ----------------------------------------------------------------------------
//   PageHeader   N07/N09: one gray '‹ Herds' back link above the title on every
//                sub-view (no Home buttons in page bodies); a Title Case H1 at
//                28–34px / 800 matching its tab; one gray summary line; the
//                page's actions on the right. P01: a read-only user sees one
//                gray 'View only — …' line under the title.
//   RecordHeader R01: the record title, one status badge, a gray meta line
//                joined by '·', related links as secondary buttons on the
//                right; N08 prev/next sits inline next to the title.
//   SequenceNav  N08: inline '‹ 3 of 12 ›' — the shared SequenceControl (no
//                edge chevrons; ←/→ outside fields, widgets and open modals).
//   PageFrame    widths: 1180 for pages, 960 for record pages and forms
//                (Q41), 980 for Home (Q16), 560 for field forms (Q20); side
//                padding 20–24px desktop, 16px phone (index.html).
//   usePageTitle Q41: the tab title is 'Page · Program · WCF'.
// ============================================================================

export type PageWidth = 'page' | 'record' | 'home' | 'field';

export function PageFrame({width = 'page', children}: {width?: PageWidth; children: React.ReactNode}) {
  return (
    <div data-page-frame={width} className="wcf-page-frame">
      {children}
    </div>
  );
}

export type BackLink = {label: string; onBack: () => void};

function BackLinkButton({back}: {back: BackLink}) {
  return (
    <button type="button" className="wcf-back-link" data-page-back="1" onClick={back.onBack}>
      <span aria-hidden="true">‹</span> {back.label}
    </button>
  );
}

export function PageHeader({
  back,
  title,
  summary,
  actions,
  viewOnly,
}: {
  back?: BackLink | null;
  title: React.ReactNode;
  summary?: React.ReactNode;
  actions?: React.ReactNode;
  viewOnly?: React.ReactNode;
}) {
  return (
    <header data-page-header="1" className="wcf-page-header">
      {back && <BackLinkButton back={back} />}
      <div className="wcf-page-header__row">
        <div className="wcf-page-header__text">
          <h1 className="wcf-page-title">{title}</h1>
          {summary != null && summary !== '' && <p className="wcf-page-summary">{summary}</p>}
          {viewOnly != null && viewOnly !== '' && (
            <p className="wcf-page-summary" data-view-only="1">
              View only — {viewOnly}
            </p>
          )}
        </div>
        {actions && <div className="wcf-page-header__actions">{actions}</div>}
      </div>
    </header>
  );
}

export type SequencePosition = {
  index: number;
  total: number;
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
};

// '3 of 12' for the 0-based index 2 of 12; empty when the control is hidden
// (fewer than two records, or a position outside the list).
export const sequenceLabel = sequencePosition;

// N08 prev/next for a record header: the one shared SequenceControl, so the
// markup (data-record-seq-*), the ←/→ rules and the hide rule are the same as
// RecordSequenceNav's.
export function SequenceNav(props: SequencePosition) {
  return <SequenceControl {...props} />;
}

export function RecordHeader({
  back,
  title,
  badge,
  meta = [],
  related,
  sequence,
  viewOnly,
}: {
  back?: BackLink | null;
  title: React.ReactNode;
  badge?: React.ReactNode;
  meta?: React.ReactNode[];
  related?: React.ReactNode;
  sequence?: SequencePosition | null;
  viewOnly?: React.ReactNode;
}) {
  const parts = meta.filter((m) => m != null && m !== false && m !== '');
  return (
    <header data-record-header="1" className="wcf-page-header">
      {back && <BackLinkButton back={back} />}
      <div className="wcf-page-header__row">
        <div className="wcf-page-header__text">
          <div className="wcf-record-title-row">
            <h1 className="wcf-page-title">{title}</h1>
            {badge}
            {sequence && <SequenceNav {...sequence} />}
          </div>
          {parts.length > 0 && (
            <p className="wcf-page-summary" data-record-meta="1">
              {parts.map((m, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ' · '}
                  {m}
                </React.Fragment>
              ))}
            </p>
          )}
          {viewOnly != null && viewOnly !== '' && (
            <p className="wcf-page-summary" data-view-only="1">
              View only — {viewOnly}
            </p>
          )}
        </div>
        {related && <div className="wcf-page-header__actions">{related}</div>}
      </div>
    </header>
  );
}

// 'Herds · Cattle · WCF'; a page outside a program is 'Tasks · WCF'.
export function pageTitle(page: string | null | undefined, program?: string | null): string {
  return [page, program, 'WCF'].filter((p) => p != null && String(p).trim() !== '').join(' · ');
}

export function usePageTitle(page: string | null | undefined, program?: string | null): void {
  const title = pageTitle(page, program);
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
